import * as httpRequest from '../commons/httpRequest';
import * as appiumCapabilitiesBuilder from '../commons/appiumCapabilitiesBuilder';
import { BaseWorker } from './BaseWorker';
import { download } from '../utils';
import { getLogger } from '../commons/logger';
import { reporter } from '../reports/reporter';
import { AppiumTestPlayer } from '../player/appiumTestPlayer';
import { getSessionPlayer } from '../commons/getSessionPlayerRequire';
import { checkIfTmaIsReady, getDeviceStatusFromTma } from '../utils/tmaUtils';
import { mobileGridService } from '../services/mobileGridService';
import type { FailedSessionPlayerTestResult } from 'clickim/src/background/session/sessionPlayer';
import type { SerializedGrid } from '../services/gridService';
import type { TestResult } from 'clickim/src/common/models/TestResult';
import type { NativeAppData, TestRunHandler } from '../testRunHandler';
import type { TmaAppiumSession } from 'clickim/src/background/session/MobileAgentSessionManager';
import type { PlaybackMobileDevice } from 'services/src/grid/mobileGridService';

const logger = getLogger('worker-appium');

class LocalDeviceNotFoundError extends Error { }
class FailedToInstallDeviceError extends Error { }
class FailedToCreateDriverSessionError extends Error { }

interface InstallAppResponse {
    bundleId: string;
    androidPackage: string;
    activities?: any[];
    version: string;
}

interface IosApp {
    id: string;
    name: string;
    version: string;
}

interface AndroidApp {
    packageName: string;
    activity: string;
    name: string;
    version: string;
}

export class WorkerLocalTma extends BaseWorker<AppiumTestPlayer> {
    private sessionData!: TmaAppiumSession;
    private device!: PlaybackMobileDevice;
    override initPlayer(testRunHandler: TestRunHandler) {
        return new AppiumTestPlayer(
            this.id,
            testRunHandler.runParams,
            this.options.shouldMonitorPerformance,
            undefined,
            testRunHandler.retryCount,
            testRunHandler.previousTestResultId,
        );
    }

    private async initAndGetAppiumAPI(appiumTestPlayer: AppiumTestPlayer) {
        if (appiumTestPlayer.sessionPlayer.playbackManager.appiumApi) {
            return appiumTestPlayer.sessionPlayer.playbackManager.appiumApi;
        }

        const { AppiumApi } = getSessionPlayer();
        const { DOMParser } = await import('linkedom');
        const appiumApi = new AppiumApi(this.sessionData.serverAddress, this.sessionData.sessionId, DOMParser, false);
        appiumTestPlayer.sessionPlayer.playbackManager.appiumApi = appiumApi;

        return appiumApi;
    }

    override async getBrowserOnce(testRunHandler: TestRunHandler, customExtensionLocalLocation: string | undefined, appiumTestPlayer: AppiumTestPlayer, gridInfo: SerializedGrid) {
        reporter.onGetSession(this.id, this.testName, testRunHandler.runMode);

        if (!this.options.localTmaUrl || !this.options.deviceUdid) {
            throw new Error('Missing localTmaUrl or deviceUdid');
        }

        const { localTmaUrl, deviceUdid, projectData } = this.options;
        await checkIfTmaIsReady(localTmaUrl);

        const { project: projectId, authData: { token = '' } = {} } = this.options;
        const { driver } = appiumTestPlayer;
        const projectType = projectData!.type!;
        const appId = this.options.appId;
        if (appId) {
            const { mobileApp } = await mobileGridService.getMobileAppDataByAppId({ appId, projectId, token });
            // Override the nativeApp associated with the testRunHandler.
            testRunHandler.nativeApp = mobileApp!;
        }
        const androidActivityWait = projectType === 'android' ? testRunHandler.androidActivityWait : null;
        const nativeAppMetadata = testRunHandler.nativeAppMetadata;
        const appPath: string | null = testRunHandler.downloadableAppPublicLink;
        const appPackageNameOrBundleId = testRunHandler.appPackageNameOrBundleId;
        if (!nativeAppMetadata) {
            throw new Error('Application not specified. Please provide the required application for execution and try again.');
        }

        if (!appPackageNameOrBundleId) {
            throw new Error('The application appears to be corrupted. Please ensure the application is in a valid state before running the test.');
        }
        const [{ appInstalled, version }, deviceStatus] = await Promise.all([
            this.getAppStateFromDevice(localTmaUrl, deviceUdid, appPackageNameOrBundleId),
            getDeviceStatusFromTma(localTmaUrl, deviceUdid),
        ]).catch(err => {
            this.handleDeviceNotFoundError(err);
            throw err;
        });
        logger.info('device status and app state', { deviceStatus, appInstalled, version, executionId: this.executionId, testResultId: this.testResultId });

        if (!appInstalled && testRunHandler.isAppFromDevice && !appId) {
            throw new Error('The application is not installed on the target device. Please ensure the application is installed and try again.');
        }

        if (!appInstalled && appPath) {
            await this.installApp(localTmaUrl, deviceUdid, appPath);
            logger.info('Application installed successfully', { deviceUdid, executionId: this.executionId, testResultId: this.testResultId });
        }

        try {
            const sessionTags: appiumCapabilitiesBuilder.SessionTags = {
                testName: this.testName,
                executionId: this.executionId,
                testResultId: this.testResultId!,
                project: `${this.options.projectData?.name}-${this.options.project}`,
            };
            const capabilities = appiumCapabilitiesBuilder.buildAppiumLocalTmaCapabilities({
                projectType,
                nativeAppMetadata,
                appPath,
                androidActivityWait,
                sessionTags,
                gridInfo,
                options: this.options,
                testRunConfig: this.testRunConfig,
                customCapabilities: testRunHandler.customCapabilities,
            });
            const tmaSessionSettings = {
                capabilities: {
                    udid: deviceUdid,
                    ...capabilities,
                },
            };
            logger.info('requesting new appium session from TMA', { tmaSessionSettings, executionId: this.executionId, testResultId: this.testResultId });
            const sessionData = await this.requestNewAppiumSessionFromTma(localTmaUrl, tmaSessionSettings, nativeAppMetadata);

            this.sessionData = sessionData;

            const activeSession = await driver.attach({
                sessionId: sessionData.sessionId,
                capabilities,
            });

            this.device = await mobileGridService.updateTmaDeviceDetails(deviceUdid, this.options, testRunHandler);
            logger.info(`init new appium session with local TMA testName: ${this.testName}`,
                {
                    sessionId: activeSession.sessionId,
                    testResultId: this.testResultId,
                    nativeAppMetadata,
                    sessionCaps: activeSession.capabilities,
                });
        } catch (err) {
            //TODO:  catch app status validation
            logger.error('failed to start application', { err });
            throw err;
        }
    }

    private async requestNewAppiumSessionFromTma(localTmaUrl: string, settings: { capabilities: any }, appMetadata: NativeAppData): Promise<TmaAppiumSession> {
        try {
            const sessionData = await httpRequest.post<Omit<TmaAppiumSession, 'env'>>({
                url: `${localTmaUrl}/api/session`,
                headers: { 'Content-Type': 'application/json' },
                body: settings,
                timeout: 60_000,
            });
            const appInfo = { name: appMetadata.name, version: appMetadata.version };
            // @ts-expect-error The type comes from Clickim with the value - but we can't import value from Clickim.
            return { ...sessionData, appInfo, env: 'local-tma' };
        } catch (error) {
            this.handleDeviceNotFoundError(error);
            const message: unknown = error.response?.body?.message;
            if (error.status === 500 && typeof message === 'string' && message.startsWith('Failed to create driver session')) {
                throw new FailedToCreateDriverSessionError(message);
            }
            // Can get here as well if using localhost instead of 127.0.0.1 since TMA doesn't listen to ipv6 :(
            throw error;
        }
    }

    private baseDevicePath = (localTmaUrl: string, deviceId: string) => `${localTmaUrl}/api/devices/${deviceId}`;

    private async getAppStateFromDevice(localTmaUrl: string, deviceId: string, appIdentifier: string): Promise<{ appInstalled: boolean; version: string }> {
        const path = this.options.projectData!.type === 'ios' ? 'bundles' : 'apps';
        const url = `${this.baseDevicePath(localTmaUrl, deviceId)}/${path}`;

        const results = await httpRequest.get<(IosApp | AndroidApp)[]>(url);
        const appFromResults = results.find(app => ('id' in app ? app.id === appIdentifier : app.packageName === appIdentifier));
        return !appFromResults ? { appInstalled: false, version: '' } : { appInstalled: true, version: appFromResults.version };
    }

    private async installApp(localTmaUrl: string, deviceId: string, appPath: string) {
        try {
            const app = await download(appPath);
            await httpRequest.postForm<InstallAppResponse>(`${this.baseDevicePath(localTmaUrl, deviceId)}/apps`, {}, { file: { buffer: app.body, fileName: 'app' } });
            // todo: check the app was installed.
        } catch (error) {
            this.handleDeviceNotFoundError(error);
            const tmaErrorMessage: string | undefined = error.response?.body?.message;
            let message = 'Failed to install app';
            if (tmaErrorMessage) {
                message = `${message}: ${tmaErrorMessage}`;
            }
            throw new FailedToInstallDeviceError(message, { cause: error });
        }
    }

    private handleDeviceNotFoundError(error: any) {
        const tmaErrorMessage: unknown = error.response?.body?.message;
        if (error.status === 404 && typeof tmaErrorMessage === 'string' && tmaErrorMessage?.startsWith('Ensure that the requested device')) {
            throw new LocalDeviceNotFoundError(`Device Unavailable: Please ensure that the specified device ${this.options.deviceUdid} is currently connected`);
        }
    }

    async runTestOnce(testRunHandler: TestRunHandler, appiumTestPlayer: AppiumTestPlayer) {
        const { manifestVersion, localAssetService } = getSessionPlayer();
        const { sessionPlayer } = appiumTestPlayer;
        const version = manifestVersion || 'runner';

        reporter.onWaitToTestComplete(this.id);

        sessionPlayer.playbackManager.executionId = testRunHandler.executionId;
        sessionPlayer.playbackManager.executionName = testRunHandler.executionName;
        sessionPlayer.playbackManager.appiumSessionData = { ...this.sessionData, device: this.device, nativeApp: testRunHandler.nativeApp as unknown as NativeAppData };
        await this.initAndGetAppiumAPI(appiumTestPlayer);
        localAssetService.initialize({ serverUrl: this.options.localRCASaver });
        const runAppiumTest = async () => {
            try {
                const testResult = await new Promise<TestResult | FailedSessionPlayerTestResult>((resolve, reject) => {
                    sessionPlayer.playByTestId({
                        testId: this.testId!,
                        executionId: this.executionId,
                        resultId: this.testResultId!,
                        baseUrl: this.baseUrl,
                        userData: this.userData!,
                        version,
                        resolve,
                        isLocalRun: false,
                        overrideTestConfigId: this.overrideTestConfigId,
                        branch: this.branch!,
                        remoteRunId: testRunHandler.remoteRunId,
                    }).catch(reject);
                });
                await localAssetService.drain();
                testResult.stepsResults = null;
                testResult.resultId = this.testResultId!;
                const resultWithStats = { ...testResult, ...testRunHandler.seleniumPerfStats.getStats() };
                return resultWithStats;
            } catch (err) {
                //TODO: add test timeout and handle Timeout Error TBD
                // if (err instanceof TimeoutError && sessionPlayer.stopPlayingOnTestTimeout) {
                //     sessionPlayer.stopPlayingOnTestTimeout();
                // }
                logger.error('error while running appium tests', { err });
                throw err;
            }
        };
        try {
            await super.runTestOnce(testRunHandler, appiumTestPlayer);
            return await runAppiumTest();
        } catch (err) {
            logger.error('failed to run test once', { err });
            throw err;
        }
    }
}
